import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, AlertIcon, Badge, Box, Button, Container, Flex, Heading, HStack, IconButton,
  Input, InputGroup, InputLeftElement, Stack, Text,
} from '@chakra-ui/react';
import { DeleteIcon, SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { PublicShell } from '../public-shell/PublicShell';
import { RootState } from '../redux/store';
import { addTracks, removeTrack, clearTracks, setReferenceId, LocalTrack } from '../redux/features/localAudioSlice';
import {
  pause as playerPause, play as playerPlay, setQueue, PlayerTrack,
} from '../redux/features/playerSlice';

const MAX_FILE_MB = 50;

export default function LibraryPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { tracks, referenceId } = useSelector((s: RootState) => s.localAudio);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  // Object URLs — created on mount for each track, revoked on unmount.
  // Recomputed only when the tracks array changes reference (add/remove).
  const objectUrls = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tracks) map.set(t.id, URL.createObjectURL(t.file));
    return map;
  }, [tracks]);
  useEffect(() => () => { for (const url of objectUrls.values()) URL.revokeObjectURL(url); }, [objectUrls]);

  // If we arrived here from ?shared=1 (share-target), the SW has stashed
  // the shared audio in the mixgenie-shared-inbox Cache Storage entry —
  // drain it into the Redux slice and clear the cache.
  const cameFromShare = location.search.includes('shared=1');
  const [shareBanner, setShareBanner] = useState<null | 'ok' | 'empty' | 'error'>(() => {
    const p = new URLSearchParams(location.search).get('shared');
    if (p === '1') return 'ok';
    if (p === 'empty') return 'empty';
    if (p === 'error') return 'error';
    return null;
  });
  useEffect(() => {
    if (!cameFromShare) return;
    let cancelled = false;
    (async () => {
      try {
        if (!('caches' in window)) return;
        const cache = await caches.open('mixgenie-shared-inbox');
        const keys = await cache.keys();
        const fresh: LocalTrack[] = [];
        for (const key of keys) {
          const res = await cache.match(key);
          if (!res) continue;
          const blob = await res.blob();
          const name = res.headers.get('X-Mixgenie-Filename') || 'shared-audio';
          fresh.push({
            id: crypto.randomUUID(),
            file: new File([blob], name, { type: blob.type || 'audio/mpeg' }),
            name,
            size: blob.size,
            addedAt: Date.now(),
          });
          await cache.delete(key);
        }
        if (!cancelled && fresh.length) dispatch(addTracks(fresh));
        if (!cancelled) navigate('/library', { replace: true });
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [cameFromShare, dispatch, navigate]);

  const onFilesPicked = (list: FileList | null) => {
    if (!list) return;
    setError(null);
    const audio: LocalTrack[] = [];
    const skipped: string[] = [];
    for (const f of Array.from(list)) {
      if (!f.type.startsWith('audio/') && !/\.(mp3|m4a|wav|ogg|flac|aac)$/i.test(f.name)) {
        skipped.push(f.name);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        skipped.push(`${f.name} (too large)`);
        continue;
      }
      audio.push({
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
        size: f.size,
        addedAt: Date.now(),
      });
    }
    if (audio.length) dispatch(addTracks(audio));
    if (skipped.length) setError(t('library.skipped', 'Skipped: {{names}}', { names: skipped.slice(0, 3).join(', ') + (skipped.length > 3 ? '…' : '') }));
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tracks;
    return tracks.filter((t) => t.name.toLowerCase().includes(q));
  }, [tracks, query]);

  const useAsReference = (id: string) => {
    dispatch(setReferenceId(id));
    navigate('/generator?localRef=1');
  };

  return (
    <PublicShell>
      <Container maxW="900px" py={8}>
        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
          <Heading size="lg" color="whiteAlpha.900">
            {t('library.title', 'Music library')}
          </Heading>
          <HStack spacing={2}>
            <Button size="sm" colorScheme="pink" onClick={() => fileInputRef.current?.click()}>
              {t('library.pickFiles', '+ Add files')}
            </Button>
            <Button size="sm" variant="outline" colorScheme="whiteAlpha" onClick={() => folderInputRef.current?.click()}>
              {t('library.pickFolder', '+ Add folder')}
            </Button>
            {tracks.length > 0 && (
              <IconButton
                aria-label="Clear all"
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                color="whiteAlpha.700"
                onClick={() => confirm(t('library.confirmClear', 'Clear all tracks from this session?')) && dispatch(clearTracks())}
              />
            )}
          </HStack>
        </Flex>

        <Text color="whiteAlpha.700" fontSize="sm" mb={4}>
          {t(
            'library.hint',
            'Pick audio files from your device. Tracks stay in this browser tab only — nothing is uploaded until you use one as a reference for a generation.',
          )}
        </Text>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => onFilesPicked(e.target.files)}
        />
        <input
          ref={folderInputRef}
          type="file"
          // @ts-expect-error non-standard but widely supported
          webkitdirectory=""
          directory=""
          multiple
          style={{ display: 'none' }}
          onChange={(e) => onFilesPicked(e.target.files)}
        />

        {error && (
          <Alert status="warning" borderRadius="8px" mb={4} bg="yellow.900" color="whiteAlpha.900" borderWidth="1px" borderColor="yellow.500" fontSize="sm">
            <AlertIcon color="yellow.300" />
            {error}
          </Alert>
        )}

        {shareBanner === 'ok' && (
          <Alert status="success" borderRadius="8px" mb={4} bg="green.900" color="whiteAlpha.900" borderWidth="1px" borderColor="green.500" fontSize="sm">
            <AlertIcon color="green.300" />
            {t('library.sharedOk', 'Track added from your Share sheet.')}
          </Alert>
        )}
        {shareBanner === 'empty' && (
          <Alert status="warning" borderRadius="8px" mb={4} bg="yellow.900" color="whiteAlpha.900" borderWidth="1px" borderColor="yellow.500" fontSize="sm">
            <AlertIcon color="yellow.300" />
            {t('library.sharedEmpty', 'Nothing was shared — that item didn\'t have an audio file.')}
          </Alert>
        )}
        {shareBanner === 'error' && (
          <Alert status="error" borderRadius="8px" mb={4} bg="red.900" color="whiteAlpha.900" borderWidth="1px" borderColor="red.500" fontSize="sm">
            <AlertIcon color="red.300" />
            {t('library.sharedError', 'Could not process the shared file.')}
          </Alert>
        )}

        {tracks.length === 0 && (
          <Box bg="night.800" border="1px solid" borderColor="whiteAlpha.200" borderRadius="12px" p={8} textAlign="center">
            <Text color="whiteAlpha.700">
              {t('library.empty', 'No tracks yet. Tap “Add files” to pick songs from your device.')}
            </Text>
          </Box>
        )}

        {tracks.length > 0 && (
          <InputGroup mb={4}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="whiteAlpha.500" />
            </InputLeftElement>
            <Input
              placeholder={t('library.searchPlaceholder', 'Search by filename…')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              bg="night.800"
              borderColor="whiteAlpha.200"
              color="whiteAlpha.900"
              _placeholder={{ color: 'whiteAlpha.500' }}
            />
          </InputGroup>
        )}

        <LibraryTrackList
          filtered={filtered}
          objectUrls={objectUrls}
          referenceId={referenceId}
          onUseAsReference={useAsReference}
          onRemove={(id) => dispatch(removeTrack(id))}
          t={t as any}
        />
        {tracks.length > 0 && filtered.length === 0 && (
          <Text color="whiteAlpha.600" fontSize="sm" textAlign="center" py={4}>
            {t('library.noMatch', 'No matches for “{{query}}”', { query })}
          </Text>
        )}
      </Container>
    </PublicShell>
  );
}

// ---------------------------------------------------------------------------
// Track list — SoundCloud-style rows that play via the global bottom player.
// Split from the page component so `useSelector` on the player state doesn't
// re-render everything above (search box, upload buttons) on every timeupdate.
// ---------------------------------------------------------------------------
function LibraryTrackList({
  filtered,
  objectUrls,
  referenceId,
  onUseAsReference,
  onRemove,
  t,
}: {
  filtered: LocalTrack[];
  objectUrls: Map<string, string>;
  referenceId: string | null;
  onUseAsReference: (id: string) => void;
  onRemove: (id: string) => void;
  t: (k: string, d?: string, opts?: any) => string;
}) {
  const dispatch = useDispatch();
  const player = useSelector((s: RootState) => s.player);
  const currentTrackId = player.index >= 0 ? player.queue[player.index]?.id : null;

  const playableTracks: PlayerTrack[] = useMemo(() => (
    filtered
      .filter((tr) => !!objectUrls.get(tr.id))
      .map((tr) => ({
        id: tr.id,
        title: tr.name,
        artist: t('library.localTrack', 'Local file'),
        audioUrl: objectUrls.get(tr.id) as string,
      }))
  ), [filtered, objectUrls, t]);

  const playAt = (id: string) => {
    const idx = playableTracks.findIndex((tr) => tr.id === id);
    if (idx < 0) return;
    dispatch(setQueue({ tracks: playableTracks, startIndex: idx }));
  };

  return (
    <Stack spacing={2}>
      {filtered.map((track) => {
        const url = objectUrls.get(track.id);
        const isRef = referenceId === track.id;
        const isCurrent = currentTrackId === track.id;
        const isPlaying = isCurrent && player.isPlaying;
        const canPlay = !!url;
        return (
          <Box
            key={track.id}
            bg={isCurrent ? 'whiteAlpha.100' : 'night.800'}
            border="1px solid"
            borderColor={isCurrent ? 'pink.400' : isRef ? 'accent.400' : 'whiteAlpha.200'}
            borderRadius="10px"
            px={3}
            py={3}
          >
            <Flex align="center" gap={3}>
              <Box position="relative" flexShrink={0}>
                <Box
                  boxSize={{ base: '48px', md: '56px' }}
                  borderRadius="8px"
                  bgGradient="linear(135deg, teal.400, blue.500)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                  fontSize="2xl"
                >📁</Box>
                {canPlay && (
                  <IconButton
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    onClick={() => {
                      if (isCurrent) dispatch(isPlaying ? playerPause() : playerPlay());
                      else playAt(track.id);
                    }}
                    icon={
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d={isPlaying ? 'M6 5h4v14H6zM14 5h4v14h-4z' : 'M8 5v14l11-7z'} />
                      </svg>
                    }
                    size="sm"
                    position="absolute"
                    inset={0}
                    m="auto"
                    boxSize={{ base: '48px', md: '56px' }}
                    minW={0}
                    borderRadius="8px"
                    bg="blackAlpha.500"
                    color="white"
                    opacity={isCurrent ? 1 : 0}
                    _hover={{ opacity: 1, bg: 'blackAlpha.600' }}
                    _focus={{ opacity: 1 }}
                    transition="opacity 0.15s"
                  />
                )}
              </Box>
              <Box flex="1" minW={0}>
                <Text color="whiteAlpha.900" fontWeight={700} noOfLines={1}>{track.name}</Text>
                <HStack spacing={2} color="whiteAlpha.600" fontSize="xs" mt={0.5} wrap="wrap">
                  <Text noOfLines={1}>{t('library.localTrack', 'Local file')}</Text>
                  <Text>· {(track.size / (1024 * 1024)).toFixed(1)} MB</Text>
                  {isRef && <Badge colorScheme="pink" fontSize="9px">{t('library.reference', 'reference')}</Badge>}
                </HStack>
              </Box>
              <HStack spacing={1} flexShrink={0}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="pink"
                  onClick={() => onUseAsReference(track.id)}
                  data-cta="library:use-as-reference"
                >
                  ✨ {t('library.useAsReference', 'Use for generation')}
                </Button>
                <IconButton
                  aria-label={t('library.remove', 'Remove')}
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  color="whiteAlpha.600"
                  _hover={{ color: 'red.300', bg: 'whiteAlpha.100' }}
                  onClick={() => onRemove(track.id)}
                />
              </HStack>
            </Flex>
          </Box>
        );
      })}
    </Stack>
  );
}
